import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import client from 'utils/client';
import dayjs from 'dayjs';
import './transaction.css';
import { useModal } from 'components/Modal/useModal';
import AppDataGrid from 'components/AppDataGrid/AppDataGrid';
import { formatCurrency } from 'utils/helpers';
import ModalComponent from 'components/Modal/Modal';
import TransactionDetails from './TransactionDetails';
import ReportDialog from './ReportDialog';
import BulkSettleDialog from './BulkSettleDialog';
import { TransactionStatusPill, SettledPill, ChargePill } from 'components/StatusPill/StatusPill';
import usePagination from 'hooks/usePagination';
import FilterBar from './FilterBar';
import TransactionFiltersModal from './TransactionFiltersModal';
import { useToast } from 'components/Toast/useToast';
import { useAuth } from 'contexts/AuthContext';

// Fields the backend allows sorting on (mirror of VALID_SORT_FIELDS in the API).
const SERVER_SORTABLE = new Set([
  'reference', 'terminal_id', 'merchant_id', 'amount', 'status',
  'transaction_date', 'provider', 'settled',
]);

function Transactions() {
  const navigate = useNavigate();
  const { isMerchant } = useAuth();
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [totalCount, setTotalCount] = useState(0);
  const [reportOpen, setReportOpen] = useState(false);
  const [reportMode, setReportMode] = useState('export');
  const [sortModel, setSortModel] = useState([{ field: 'transaction_date', sort: 'desc' }]);
  const [filters, setFilters] = useState({});
  const [isFilterModalOpen, setIsFilterModalOpen] = useState(false);
  const [selectionModel, setSelectionModel] = useState([]);
  const [bulkSettleOpen, setBulkSettleOpen] = useState(false);

  // eslint-disable-next-line no-unused-vars
  const [error, setError] = useState(null);

  const { onOpen, isOpen } = useModal();
  const toast = useToast();
  const [selectedTransaction, setSelectedTransaction] = useState(null);

  const {
    paginationData: { currentPage, pageSize },
    handlePageChange,
    handlePageSizeChange,
  } = usePagination();

  useEffect(() => {
    setLoading(true);
    const params = new URLSearchParams({ page: currentPage, pageSize });
    if (sortModel[0]?.field) params.append('sortBy', sortModel[0].field);
    if (sortModel[0]?.sort) params.append('sortDir', sortModel[0].sort);
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') params.append(key, value);
    });

    client
      .get(`/transactions?${params.toString()}`)
      .then((response) => {
        setTransactions(response.data.data);
        setTotalCount(response.data.pagination.totalCount);
      })
      .catch(setError)
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, pageSize, sortModel, filters]);

  // Mirrors bulkRetrySettlement's server-side filter: today is off-limits for
  // terminals the 23:59 settlement cron owns, but open for INSTANT terminals,
  // which the cron never touches.
  const eligibleForSettlement = useMemo(() => {
    const today = dayjs().startOf('day');
    return transactions.filter(
      (t) => selectionModel.includes(t.id) && !t.settled
        && (t.terminal?.instant_settlement_enabled || dayjs(t.transaction_date).isBefore(today))
    );
  }, [selectionModel, transactions]);

  function openReport(mode) {
    setReportMode(mode);
    setReportOpen(true);
  }

  function viewDetails(transaction) {
    setSelectedTransaction(transaction);
    onOpen();
  }

  // "Customer" cell stacks name + masked PAN so one column carries both signals.
  const renderCustomer = (params) => {
    const pr = params.row?.provider_response || {};
    const name = pr.CustomerName || '—';
    const pan = pr.MaskedPAN;
    return (
      <div style={{ lineHeight: 1.25 }}>
        <div style={{ fontWeight: 500 }}>{name}</div>
        {pan && <div style={{ fontSize: 12, color: 'var(--color-text-muted)' }}>{pan}</div>}
      </div>
    );
  };

  const columns = useMemo(() => [
    {
      field: 'reference',
      headerName: 'Reference',
      width: 280,
      renderCell: (p) => <span style={{ fontFamily: 'ui-monospace, monospace', fontSize: 12 }}>{p.row.reference}</span>,
    },
    {
      field: 'status',
      headerName: 'Status',
      width: 130,
      renderCell: (p) => <TransactionStatusPill code={p.row?.status} />,
    },
    {
      field: 'amount',
      headerName: 'Amount',
      width: 130,
      align: 'right',
      headerAlign: 'right',
      valueGetter: (p) => formatCurrency(p.row?.amount),
    },
    {
      field: 'CustomerName',
      headerName: 'Customer',
      width: 200,
      sortable: false,
      renderCell: renderCustomer,
    },
    {
      field: 'transaction_date',
      headerName: 'Date',
      width: 200,
      valueGetter: (p) => dayjs(p.row.transaction_date || p.row.created_at).format('MMM DD, YYYY h:mma'),
    },
    {
      field: 'merchant_id',
      headerName: 'Merchant',
      width: 200,
      renderCell: (p) => {
        const m = p.row?.terminal?.merchant;
        const name = m ? `${m.first_name || ''} ${m.last_name || ''}`.trim() : '';
        if (!m?.id) return <span style={{ color: 'var(--color-text-muted)' }}>—</span>;
        return (
          <button
            type='button'
            onClick={(e) => { e.stopPropagation(); navigate(`/merchants/${m.id}`); }}
            style={{
              background: 'none', border: 'none', padding: 0, cursor: 'pointer',
              color: 'var(--color-brand)', textDecoration: 'underline', fontSize: 'inherit',
              fontFamily: 'inherit', textAlign: 'left',
            }}
          >
            {name || `#${m.id}`}
          </button>
        );
      },
    },
    {
      field: 'terminal_id',
      headerName: 'Terminal',
      width: 160,
      renderCell: (p) => {
        const t = p.row?.terminal;
        if (!t?.id) return <span style={{ color: 'var(--color-text-muted)' }}>—</span>;
        return (
          <button
            type='button'
            onClick={(e) => { e.stopPropagation(); navigate(`/terminals/${t.id}`); }}
            style={{
              background: 'none', border: 'none', padding: 0, cursor: 'pointer',
              color: 'var(--color-brand)', textDecoration: 'underline',
              fontFamily: 'ui-monospace, monospace', fontSize: 12, textAlign: 'left',
            }}
          >
            {t.terminal_id || `#${t.id}`}
          </button>
        );
      },
    },
    { field: 'provider', headerName: 'Provider', width: 120 },
    {
      field: 'settled',
      headerName: 'Settled',
      width: 100,
      renderCell: (p) => <SettledPill settled={!!p.row?.settled} />,
    },
    {
      field: 'charge',
      headerName: 'Charge',
      width: 110,
      sortable: false,
      renderCell: (p) => <ChargePill status={p.row?.metadata?.charge?.status} />,
    },
    {
      field: 'actions',
      headerName: '',
      width: 90,
      sortable: false,
      filterable: false,
      disableClickEventBubbling: true,
      align: 'right',
      headerAlign: 'right',
      renderCell: (p) => (
        <button type='button' className='btn-secondary' onClick={() => viewDetails(p.row)}>
          View
        </button>
      ),
    },
  ].filter((col) => !(isMerchant && col.field === 'merchant_id'))
    .map((col) => ({
      ...col,
      sortable: col.sortable ?? SERVER_SORTABLE.has(col.field),
    })), [navigate, isMerchant]);

  return (
    <>
      {isOpen ? (
        <ModalComponent title='Transaction Details' styles={{ width: '700px' }} children={<TransactionDetails data={selectedTransaction} />} />
      ) : null}

      {!isMerchant && bulkSettleOpen && (
        <BulkSettleDialog
          transactions={eligibleForSettlement}
          onClose={() => setBulkSettleOpen(false)}
          onComplete={() => {
            toast.onOpen({ message: 'Settlement retry queued successfully' });
            setTimeout(() => window.location.reload(), 1500);
          }}
        />
      )}

      <TransactionFiltersModal
        isOpen={isFilterModalOpen}
        onClose={() => setIsFilterModalOpen(false)}
        filters={filters}
        onApplyFilters={setFilters}
        onClearFilters={setFilters}
      />

      <div className='transaction'>
        <div className='transaction-title-container'>
          <h1 className='transaction-title'>Transactions</h1>
          <div className='transaction-actions'>
            {!isMerchant && eligibleForSettlement.length > 0 && (
              <button type='button' className='btn-primary' onClick={() => setBulkSettleOpen(true)}>
                Retry Settlement ({eligibleForSettlement.length})
              </button>
            )}
            <button type='button' className='btn-secondary' onClick={() => openReport('export')}>Export</button>
            <button type='button' className='btn-primary' onClick={() => openReport('report')}>Generate Report</button>
          </div>
        </div>
        {reportOpen && (
          <ReportDialog defaultMode={reportMode} filters={filters} onClose={() => setReportOpen(false)} />
        )}
        <FilterBar filters={filters} onOpenModal={() => setIsFilterModalOpen(true)} onClearFilters={setFilters} />
        <AppDataGrid
          className='transactions-list'
          rows={transactions}
          columns={columns}
          loading={loading}
          pagination
          page={currentPage - 1}
          pageSize={pageSize}
          rowCount={totalCount}
          paginationMode='server'
          onPageChange={handlePageChange}
          onPageSizeChange={handlePageSizeChange}
          sortingMode='server'
          sortModel={sortModel}
          onSortModelChange={setSortModel}
          disableColumnFilter
          checkboxSelection={!isMerchant}
          selectionModel={selectionModel}
          onSelectionModelChange={setSelectionModel}
        />
      </div>
    </>
  );
}

export default Transactions;
